/**
 * PPA (Pricing & Promo Analysis) domain types.
 *
 * These types describe the data shapes used across PPA components, modals,
 * and API payloads.  Keep component-specific prop types (e.g. PPAFieldProps)
 * in their own component files — only domain / data-shape types live here.
 */

/** The five kinds of form controls the PPA form can render. */
export type PPAInputType = 'string' | 'date_range' | 'singleselect' | 'multiselect' | 'boolean';

/** A single option that comes from the backend for dropdown fields. */
export type PPADropdownOption = {
    id: string;
    label: string;
    start_timestamp?: number;
    end_timestamp?: number;
    /** When present (e.g. from app config), used for inline date labels like Pricing Simulator create project. */
    dateArray?: [unknown, unknown];
};

/** A normalized option shape used internally by <CustomSelect>. */
export type PPASelectOption = {
    label: string;
    value: string;
    start_timestamp?: number;
    end_timestamp?: number;
    dateArray?: [unknown, unknown];
};

/** Describes one input field inside the PPA form. */
export type PPAInputConfig = {
    input_id: string;
    input_label: string;
    input_type: PPAInputType;
    info?: string;                    // tooltip text shown next to the label
    placeholder?: string;
    drop_down?: PPADropdownOption[];  // pre-defined options (e.g. time period presets)
    options?: PPASelectOption[];      // static options already in PPASelectOption shape
    attribute_type?: string;          // used to fetch dynamic options from the API
    dimension?: string;
    order_by_name?: boolean;          // sort fetched options alphabetically
};

/** Top-level config that describes the entire PPA form. */
export type PPAFeatureConfig = {
    feature_id: string;
    feature_name: string;
    feature_type: 'form';
    options: {
        inputs: PPAInputConfig[];
    };
};

/** Row / API shape passed into PriceSimulatorCreateEditModal when editing (mirrors create payload + id). */
export type PPAEditProjectParams = {
    id?: string | number;
    project_id?: string | number;
    project_name?: string;
    /** Alias used by Pricing Simulator–style parents */
    projectName?: string;
    data_source?: string;
    simulation_time_period?: {
        start_timestamp?: number;
        end_timestamp?: number;
        simulation_period_type?: string;
    };
    simulation_period_type?: string;
    project_filters?: Array<{
        dimension?: string;
        attribute_type?: string;
        attribute_ex_id?: string[];
    }>;
    is_private?: boolean;
};

/** Discriminator for parent vs child rows in the scenario manager table. */
export enum PpaTableRowType {
    Project = 'project',
    Scenario = 'scenario',
}

/** Row-level actions from scenario_manager column config (normalized to lowercase). */
export enum PpaTableRowAction {
    Edit = 'edit',
    Duplicate = 'duplicate',
    Delete = 'delete',
}

/** Scenario modal modes — create, rename (edit), or duplicate. */
export enum PpaScenarioActionType {
    Create = 'create',
    Rename = 'rename',
    Duplicate = 'duplicate',
}

/** Project modal modes — create, edit, or duplicate. */
export enum PpaProjectActionType {
    Create = 'create',
    Edit = 'edit',
    Duplicate = 'duplicate',
}

export function normalizePpaTableRowAction(actionLabel: string): PpaTableRowAction | undefined {
    const normalized = actionLabel.trim().toLowerCase();
    return (Object.values(PpaTableRowAction) as string[]).includes(normalized)
        ? (normalized as PpaTableRowAction)
        : undefined;
}

export function tableRowActionToScenarioAction(
    action: PpaTableRowAction,
): PpaScenarioActionType | undefined {
    switch (action) {
        case PpaTableRowAction.Edit:
            return PpaScenarioActionType.Rename;
        case PpaTableRowAction.Duplicate:
            return PpaScenarioActionType.Duplicate;
        default:
            return undefined;
    }
}

export function tableRowActionToProjectAction(
    action: PpaTableRowAction,
): PpaProjectActionType | undefined {
    switch (action) {
        case PpaTableRowAction.Edit:
            return PpaProjectActionType.Edit;
        case PpaTableRowAction.Duplicate:
            return PpaProjectActionType.Duplicate;
        default:
            return undefined;
    }
}
